import { Turbo } from "@hotwired/turbo-rails"

const REVEAL_FULL = 70
const REVEAL_THRESHOLD = 45
const REFRESH_THRESHOLD = 115
const MAX_PULL = 150

let startY = 0
let currentY = 0
let pulling = false
let indicator = null
let styleInjected = false

let navEl = null
let navFullHeight = 0
let revealedAtStart = false

function injectStyles() {
  if (styleInjected) return
  styleInjected = true

  const style = document.createElement("style")
  style.textContent = `
    #ptr-indicator {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 0;
      overflow: visible;
      z-index: 99999;
      pointer-events: none;
    }
    #ptr-indicator .ptr-spinner {
      width: 36px;
      height: 36px;
      border: 3px solid rgba(107, 124, 94, 0.2);
      border-top-color: #6B7C5E;
      border-radius: 50%;
      background: white;
      box-shadow: 0 2px 12px rgba(0,0,0,0.15);
      transform: translateY(-50%) scale(0);
      transition: transform 0.2s ease;
    }
    #ptr-indicator .ptr-spinner.visible {
      transform: translateY(0) scale(1);
    }
    #ptr-indicator .ptr-spinner.spinning {
      animation: ptr-spin 0.6s linear infinite;
    }
    @keyframes ptr-spin {
      to { transform: translateY(0) scale(1) rotate(360deg); }
    }
  `
  document.head.appendChild(style)
}

function createIndicator() {
  // Check if indicator still exists in DOM (Turbo might have removed it)
  if (indicator && document.body.contains(indicator)) {
    return indicator
  }

  injectStyles()

  indicator = document.createElement("div")
  indicator.id = "ptr-indicator"
  indicator.innerHTML = `<div class="ptr-spinner"></div>`
  document.body.appendChild(indicator)

  return indicator
}

function getScrollTop() {
  return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0
}

function getNav() {
  // Re-query each gesture: Turbo may have replaced the element.
  if (navEl && document.body.contains(navEl)) return navEl
  navEl = document.querySelector(".mobile-navbar-shell")
  return navEl
}

function navRevealed() {
  const nav = getNav()
  return nav ? nav.classList.contains("mobile-navbar-revealed") : false
}

function revealNav() {
  const nav = getNav()
  if (!nav) return
  nav.classList.remove("mobile-navbar-dragging")
  nav.style.maxHeight = ""
  nav.classList.add("mobile-navbar-revealed")
}

function hideNav() {
  const nav = getNav()
  if (!nav) return
  nav.classList.remove("mobile-navbar-dragging", "mobile-navbar-revealed")
  nav.style.maxHeight = ""
}

function trackNav(pullDistance) {
  const nav = getNav()
  if (!nav) return

  if (!nav.classList.contains("mobile-navbar-dragging")) {
    navFullHeight = nav.scrollHeight || 60
    nav.classList.add("mobile-navbar-dragging")
  }

  const progress = Math.min(pullDistance / REVEAL_FULL, 1)
  nav.style.maxHeight = `${navFullHeight * progress}px`
}

function onTouchStart(e) {
  if (getScrollTop() > 5) return

  startY = e.touches[0].clientY
  currentY = startY
  pulling = true
  revealedAtStart = navRevealed()

  createIndicator()
}

function onTouchMove(e) {
  if (!pulling) return
  if (getScrollTop() > 5) {
    pulling = false
    return
  }

  currentY = e.touches[0].clientY
  const diff = currentY - startY

  if (diff < 0) {
    pulling = false
    return
  }

  const pullDistance = Math.min(diff * 0.5, MAX_PULL)

  // Stage 1: nav follows the finger and reveals (skip if already open).
  if (!revealedAtStart) trackNav(pullDistance)

  // Stage 2: refresh spinner only past the reveal zone.
  const ind = createIndicator()
  const spinner = ind.querySelector(".ptr-spinner")
  const refreshPull = pullDistance - REVEAL_FULL

  if (refreshPull > 0) {
    ind.style.height = `${refreshPull}px`
    spinner.classList.add("visible")
    spinner.classList.toggle("spinning", pullDistance >= REFRESH_THRESHOLD)
  } else {
    ind.style.height = "0"
    spinner.classList.remove("visible", "spinning")
  }
}

function onTouchEnd() {
  if (!pulling) return
  pulling = false

  const diff = currentY - startY
  const pullDistance = Math.min(diff * 0.5, MAX_PULL)
  const ind = createIndicator()
  const spinner = ind.querySelector(".ptr-spinner")

  const resetSpinner = () => {
    spinner.classList.remove("visible", "spinning")
    ind.style.height = "0"
  }

  if (pullDistance >= REFRESH_THRESHOLD) {
    hideNav()
    ind.style.height = "60px"

    Turbo.visit(window.location.href, { action: "replace" })

    setTimeout(resetSpinner, 800)
  } else if (revealedAtStart || pullDistance >= REVEAL_THRESHOLD) {
    revealNav()
    resetSpinner()
  } else {
    hideNav()
    resetSpinner()
  }
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    navigator.standalone === true
  )
}

function init() {
  if (!isStandalone()) {
    console.log("[PTR] Not in standalone mode, pull-to-refresh disabled")
    return
  }

  console.log("[PTR] Pull-to-refresh + nav reveal enabled")
  document.addEventListener("touchstart", onTouchStart, { passive: true })
  document.addEventListener("touchmove", onTouchMove, { passive: true })
  document.addEventListener("touchend", onTouchEnd, { passive: true })
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init)
} else {
  init()
}
